import { observer } from "mobx-react-lite"
import type { ReactNode } from "react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

export interface SelectOption {
  id: string
  label: string
}

interface PageHeaderProps {
  title: string
  subtitle?: string
  className?: string
  children?: ReactNode
  selectOptions?: SelectOption[]
  selectedOptionId?: string
  onSelectChange?: (optionId: string) => void
  selectPlaceholder?: string
}

export const PageHeader = observer(
  ({
    title,
    subtitle,
    className,
    children,
    selectOptions,
    selectedOptionId,
    onSelectChange,
    selectPlaceholder = "Select an option",
  }: PageHeaderProps) => {
    return (
      <div className={cn("space-y-6", className)}>
        {/* Header section with title/subtitle and optional select */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          {/* Title and subtitle */}
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">{title}</h1>
            {subtitle && <p className="text-sm sm:text-base text-muted-foreground">{subtitle}</p>}
          </div>

          {/* Optional select dropdown */}
          {selectOptions && selectOptions.length > 0 && (
            <div className="flex items-center">
              <Select value={selectedOptionId} onValueChange={onSelectChange}>
                <SelectTrigger
                  className="w-full sm:w-[200px]"
                  aria-label={selectPlaceholder || "Select an option"}
                >
                  <SelectValue placeholder={selectPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {selectOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Children content */}
        {children}
      </div>
    )
  },
)

PageHeader.displayName = "PageHeader"
